// frontend/client/src/components/PricingPage.js
// Dashboard-ready pricing page (Freemium + 3 tiers)
// - Correct 3-tier pricing via subscriptionGates.js
// - Updated to Violet/Blue Brand Aesthetic & Plus Jakarta Sans
// - Live Stripe integration via apiClient.js

import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, Crown, Sparkles, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { SUBSCRIPTION_TIERS, TIER_PRICING } from '../utils/subscriptionGates';
import { api } from '../services/apiClient';

export default function PricingPage({ onUpgrade }) {
  const { userData } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [billingCycle, setBillingCycle] = useState('monthly'); 
  const [loadingTier, setLoadingTier] = useState(null); 

  const inDashboard = location.pathname.startsWith('/dashboard');
  const currentPlan = userData?.subscriptionPlan || SUBSCRIPTION_TIERS.FREEMIUM;

  const plans = useMemo(() => {
    return [
      SUBSCRIPTION_TIERS.FREEMIUM,
      SUBSCRIPTION_TIERS.CREATOR,
      SUBSCRIPTION_TIERS.GROWTH,
      SUBSCRIPTION_TIERS.BUSINESS,
    ];
  }, []);

  const theme = {
    pageBg: isDarkMode ? 'bg-gray-950' : 'bg-gray-50',
    cardBg: isDarkMode ? 'bg-gray-900' : 'bg-white',
    border: isDarkMode ? 'border-gray-800' : 'border-gray-200',
    textPrimary: isDarkMode ? 'text-white' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
  };

  const getPrice = (tier) => {
    const p = TIER_PRICING?.[tier];
    if (!p) return { amount: 0, suffix: '/mo' };
    if (billingCycle === 'annual') return { amount: p.annualPrice, suffix: '/yr' };
    return { amount: p.monthlyPrice, suffix: '/mo' };
  };

  const handleUpgrade = async (tier) => {
    if (typeof onUpgrade === 'function') {
      onUpgrade(tier, billingCycle);
      return;
    }

    const p = TIER_PRICING?.[tier];
    
    if (!p || !p.stripeMonthlyPriceId) {
      sessionStorage.setItem('upgrade_intent_tier', tier);
      sessionStorage.setItem('upgrade_intent_cycle', billingCycle);

      if (inDashboard) {
        navigate('/dashboard/settings', { replace: false });
      } else {
        navigate('/contact-us', { replace: false });
      }
      return;
    }

    setLoadingTier(tier);
    try {
      // apiClient returns { success: true, ...data } directly — no .data wrapper
      const response = await api.post('/stripe/create-checkout-session', {
        priceId: p.stripeMonthlyPriceId,
        successUrl: `${window.location.origin}/dashboard?checkout=success`,
        cancelUrl: `${window.location.origin}/pricing?checkout=cancelled`
      });

      if (response.success && response.url) {
        window.location.href = response.url;
      } else {
        alert(response.error || 'Failed to initialize checkout.');
      }
    } catch (error) {
      // This only catches unexpected JS errors — apiClient swallows HTTP errors into response objects
      console.error('Checkout unexpected error:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingTier(null);
    }
  };

  const TierBadge = ({ tier }) => {
    if (tier === SUBSCRIPTION_TIERS.GROWTH) {
      return (
        <span className="inline-flex items-center gap-2 text-xs font-black px-3 py-1 rounded-full bg-violet-600 text-white">
          <Sparkles className="w-3.5 h-3.5" /> Most Popular
        </span>
      );
    }
    if (tier === SUBSCRIPTION_TIERS.BUSINESS) {
      return (
        <span className="inline-flex items-center gap-2 text-xs font-black px-3 py-1 rounded-full bg-blue-600 text-white">
          <Crown className="w-3.5 h-3.5" /> Power Tier
        </span>
      );
    }
    return null;
  };

  const featuresByTier = {
    [SUBSCRIPTION_TIERS.FREEMIUM]: [
      'Try the workflow with limited usage',
      'Basic content creation',
      'Explore the dashboard',
    ],
    [SUBSCRIPTION_TIERS.CREATOR]: [
      '1,500 credits/month',
      'AI captioning + publishing',
      'Starter scheduling + previews',
    ],
    [SUBSCRIPTION_TIERS.GROWTH]: [
      '6,000 credits/month',
      'Advanced analytics',
      'AI video tools',
    ],
    [SUBSCRIPTION_TIERS.BUSINESS]: [
      '20,000 credits/month',
      'Team + higher throughput',
      'Priority support',
    ],
  };

  const title = inDashboard ? 'Upgrade your plan' : 'Pricing';

  return (
    <div 
      className={`${theme.pageBg} min-h-screen`}
      style={{ fontFamily: 'var(--font-body, "Plus Jakarta Sans", sans-serif)' }}
    >
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-violet-600 to-blue-500 text-white flex items-center justify-center shadow-lg">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <h1 className={`text-3xl md:text-4xl font-black ${theme.textPrimary}`}>{title}</h1>
            </div>

            <p className={`${theme.textSecondary} text-base md:text-lg max-w-2xl`}>
              Choose the tier that matches your output. Start on the free tier — upgrade anytime.
            </p>

            {inDashboard && (
              <p className={`mt-3 text-sm ${theme.textSecondary}`}>
                Current plan:{' '}
                <span className={`font-black ${theme.textPrimary}`}>
                  {String(currentPlan).toUpperCase()}
                </span>
              </p>
            )}
          </div>

          <div className="flex justify-start md:justify-end">
            <div className="inline-flex bg-gray-200 rounded-2xl p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 text-sm font-black rounded-xl transition-all ${
                  billingCycle === 'monthly' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 text-sm font-black rounded-xl transition-all ${
                  billingCycle === 'annual' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                }`}
              >
                Annual (2 months free)
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {plans.map((tier) => {
            const price = getPrice(tier);
            const isCurrent = tier === currentPlan;
            const p = TIER_PRICING?.[tier];

            const highlight =
              tier === SUBSCRIPTION_TIERS.GROWTH
                ? 'border-4 border-violet-500 shadow-2xl'
                : `border ${theme.border}`;

            return (
              <div
                key={tier}
                className={`${theme.cardBg} rounded-3xl ${highlight} p-7 flex flex-col`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className={`text-xl font-black ${theme.textPrimary}`}>
                      {tier === SUBSCRIPTION_TIERS.FREEMIUM ? 'Free' : tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </h3>
                    <p className={`text-sm ${theme.textSecondary} mt-1`}>
                      {p?.description || ''}
                    </p>
                  </div>
                  <TierBadge tier={tier} />
                </div>

                <div className="mb-5">
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-black text-violet-600">
                      {tier === SUBSCRIPTION_TIERS.FREEMIUM ? '$0' : `$${price.amount}`}
                    </span>
                    <span className={`${theme.textSecondary} font-bold`}>{price.suffix}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-7">
                  {(featuresByTier[tier] || []).map((f) => (
                    <li key={f} className={`flex gap-2 ${theme.textSecondary} font-semibold`}>
                      <Check className="w-5 h-5 text-violet-600 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  {tier === SUBSCRIPTION_TIERS.FREEMIUM ? (
                    inDashboard ? (
                      <Link
                        to="/dashboard/content-lab"
                        className="block w-full text-center py-3 rounded-xl font-black bg-gray-900 text-white hover:opacity-95"
                      >
                        Go to Content Lab
                      </Link>
                    ) : (
                      <Link
                        to="/signup"
                        className="block w-full text-center py-3 rounded-xl font-black bg-gray-900 text-white hover:opacity-95"
                      >
                        Get Started
                      </Link>
                    )
                  ) : (
                    <button
                      onClick={() => !isCurrent && handleUpgrade(tier)}
                      disabled={isCurrent || loadingTier === tier}
                      className={`w-full py-3 rounded-xl font-black transition ${
                        isCurrent
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-violet-600 to-blue-500 text-white hover:brightness-105 shadow-lg'
                      }`}
                    >
                      {loadingTier === tier ? 'Loading...' : isCurrent ? 'Current Plan' : 'Upgrade'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}